import { API } from '../helpers/api';

export type HologramScope = 'admin' | 'user';

export type HologramVisualKind = 'image' | 'character' | 'pet' | 'scene';
export type HologramPersonality = 'quiet' | 'lively' | 'affectionate';

export type HologramCoverPending = {
  kind: 'image' | 'gif' | string;
  frameCount: number;
  frameUrls: string[];
  sourceName: string;
  visualKind?: HologramVisualKind | string;
  personality?: HologramPersonality | string;
  headX?: number;
  headY?: number;
};

export type HologramCover = {
  id: number;
  deviceId: number;
  deviceMac: string;
  boardType: string;
  kind: 'image' | 'gif' | string;
  frameCount: number;
  frameUrls: string[];
  sourceName: string;
  visualKind?: HologramVisualKind | string;
  personality?: HologramPersonality | string;
  headX?: number;
  headY?: number;
  version: number;
  pushed: boolean;
  pending?: HologramCoverPending | null;
  updatedAt: string;
};

export type HologramPreset = {
  id: string;
  title: string;
  category: string;
  kind: 'image' | 'gif' | string;
  thumbUrl: string;
  sourceUrl: string;
  sizeBytes: number;
};

type CoverResponse = {
  code?: number;
  success?: boolean;
  message?: string;
  data?: HologramCover | null;
};

type PresetListResponse = {
  success?: boolean;
  message?: string;
  data?: HologramPreset[];
};

function base(scope: HologramScope): string {
  return scope === 'admin' ? '/api/admin/hologram-cover' : '/api/user/hologram-cover';
}

export async function getHologramCover(deviceId: number, scope: HologramScope = 'admin'): Promise<CoverResponse> {
  const res = await API.get<CoverResponse>(base(scope), {
    params: { device_id: deviceId },
  });
  return res.data;
}

export async function uploadHologramCover(
  deviceId: number,
  file: File,
  scope: HologramScope = 'admin',
  visualKind: HologramVisualKind = 'image',
  personality: HologramPersonality = 'quiet',
): Promise<CoverResponse> {
  const body = new FormData();
  body.append('device_id', String(deviceId));
  body.append('file', file);
  body.append('visual_kind', visualKind);
  body.append('personality', personality);
  const res = await API.post<CoverResponse>(base(scope), body);
  return res.data;
}

export async function applyHologramCover(deviceId: number, scope: HologramScope = 'admin'): Promise<CoverResponse> {
  const res = await API.post<CoverResponse>(`${base(scope)}/apply`, null, {
    params: { device_id: deviceId },
  });
  return res.data;
}

export async function deleteHologramCover(deviceId: number, scope: HologramScope = 'admin'): Promise<CoverResponse> {
  const res = await API.delete<CoverResponse>(base(scope), {
    params: { device_id: deviceId },
  });
  return res.data;
}

export async function listHologramPresets(scope: HologramScope = 'admin'): Promise<HologramPreset[]> {
  const res = await API.get<PresetListResponse>(`${base(scope)}/presets`);
  return Array.isArray(res.data?.data) ? res.data.data : [];
}

export async function applyHologramPreset(
  deviceId: number,
  presetId: string,
  scope: HologramScope = 'admin',
): Promise<CoverResponse> {
  const res = await API.post<CoverResponse>(`${base(scope)}/preset`, null, {
    params: { device_id: deviceId, preset_id: presetId },
  });
  return res.data;
}

export async function listMyHologramDevices(): Promise<Record<string, unknown>[]> {
  const res = await API.get('/api/user/device/list', {
    skipGlobalErrorHandler: true,
  } as Parameters<typeof API.get>[1]);
  const rows = Array.isArray(res.data?.data) ? res.data.data : [];
  return rows.filter(
    (row: Record<string, unknown>) =>
      String(row.boardType || row.board_type || '').trim().toLowerCase() === 'quanxicang-s3',
  );
}
